// packages/shared-components/src/components/DataTable/DataTable.tsx

import React, { useMemo } from "react";
import type { TableColumn, TableProps } from "./types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "../Tooltip";
import "./Table.css";

function pickCellValue<RecordType extends object>(
  record: RecordType,
  dataIndex: TableColumn<RecordType>["dataIndex"]
): unknown {
  if (dataIndex === undefined) return undefined;
  return (record as Record<string, unknown>)[dataIndex as string];
}

function resolveRowKey<RecordType extends object>(
  record: RecordType,
  index: number,
  rowKey: TableProps<RecordType>["rowKey"]
): string | number {
  if (typeof rowKey === "function") {
    return rowKey(record, index);
  }
  if (typeof rowKey === "string") {
    const v = (record as Record<string, unknown>)[rowKey];
    if (v !== undefined && v !== null) return v as string | number;
  }
  const fallback = (record as Record<string, unknown>).key;
  if (fallback !== undefined && fallback !== null)
    return fallback as string | number;
  return index;
}

export function Table<RecordType extends object = Record<string, unknown>>(
  props: TableProps<RecordType>
) {
  const {
    columns,
    dataSource,
    rowKey,
    size = "middle",
    bordered = true,
    locale,
    className,
  } = props;
  const { t } = useTranslation();

  const emptyText = locale?.emptyText ?? t("keyboard.boardConfig.emptyData");

  const colCount = columns.length;

  const tableClass = useMemo(
    () =>
      [
        "shared-data-table",
        `shared-data-table--${size}`,
        bordered ? "shared-data-table--bordered" : "",
        className || "",
      ]
        .filter(Boolean)
        .join(" "),
    [size, bordered, className]
  );

  if (!columns.length) {
    return <div className={tableClass}>{emptyText}</div>;
  }

  return (
    <div className={tableClass} role="table">
      <table className="shared-data-table__inner">
        <thead className="shared-data-table__thead">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`shared-data-table__th shared-data-table__th--${col.align || "left"}`}
                style={
                  col.width !== undefined ? { width: col.width } : undefined
                }
                scope="col"
              >
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="shared-data-table__tbody">
          {dataSource.length === 0 ? (
            <tr>
              <td
                className="shared-data-table__td shared-data-table__empty"
                colSpan={colCount}
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            dataSource.map((record, rowIndex) => (
              <tr
                key={String(resolveRowKey(record, rowIndex, rowKey))}
                className="shared-data-table__tr"
              >
                {columns.map((col) => {
                  const raw = pickCellValue(record, col.dataIndex);
                  const content = col.render
                    ? col.render(raw, record, rowIndex)
                    : raw;

                  const tooltipText = raw != null ? String(raw) : "";

                  let cellContent: React.ReactNode;
                  if (col.ellipsis || col.showTooltip) {
                    const body = col.ellipsis ? (
                      <div className="shared-data-table__td-ellipsis">
                        {content as React.ReactNode}
                      </div>
                    ) : (
                      (content as React.ReactNode)
                    );

                    cellContent = col.showTooltip ? (
                      <Tooltip
                        text={tooltipText}
                        placement="top"
                        maxWidth={300}
                        zIndex={1001}
                        usePortal
                      >
                        {body}
                      </Tooltip>
                    ) : (
                      body
                    );
                  } else {
                    cellContent = content as React.ReactNode;
                  }

                  return (
                    <td
                      key={col.key}
                      className={`shared-data-table__td shared-data-table__td--${col.align || "left"}`}
                      style={
                        col.width !== undefined
                          ? { width: col.width }
                          : undefined
                      }
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
